/**
 * useFormulary.ts — the licensed-strength read for one medication family.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: prescribing truth
 * (2026-09-12).
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * WHY THIS HOOK EXISTS, AND WHY IT FAILS LOUDLY.
 *
 * Until now no human anywhere in this product chose a medication STRENGTH. A
 * clinician picked a product FAMILY and typed one free-text box, so every
 * surface downstream that needed a strength invented one: orders enriched the
 * prescription from a catalogue figure that is really a PRICING ANCHOR (the
 * dearest variant each family is priced against — a commercial number), and the
 * pharmacy console regex-scraped a strength out of a product name. Two live
 * patients had a dispensing document printed with a figure several times the
 * dose treatment starts at.
 *
 * The fix is to ask the prescriber, and to get the list from the one place that
 * holds licensed variants as transcribed DATA: `GET /v1/prices/formulary`.
 *
 * SO A FAILED READ MUST BLOCK, NOT DEGRADE. Every other list-backed control in
 * this portal falls back to free text when its read fails, and that is correct
 * for a product key or a clinic name. It is NOT correct here: a free-text
 * strength box is the exact hole this lane exists to close, and reopening it for
 * an afternoon's outage would put the same defect back on the same screen. The
 * consumers of this hook render an error and refuse to submit.
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * R28: no medicine name is ever hard-coded in this repo — every string on this
 * surface arrives at runtime from the API, including in the tests.
 */
import { useCallback, useEffect, useState } from 'react';
import { getFormulary } from '../api/prices';
import type { MedicationFormulary } from '../api/types';

/**
 * What the picker knows about one family's strengths.
 *
 * `idle` is a real state, not a spinner in disguise: a prescription line whose
 * medication has not been chosen yet has no family to ask about, and showing
 * "loading" for a question nobody has asked reads as a stuck screen.
 */
export type FormularyState =
  | { state: 'idle' }
  | { state: 'loading' }
  | { state: 'ready'; family: MedicationFormulary; source: string }
  | { state: 'error' };

/**
 * Read the licensed variants for ONE family.
 *
 * ⚠️ THE RESPONSE IS MATCHED, NOT ASSUMED. The route takes a `productKey` and is
 * expected to answer with that family, but this hook still looks the family up
 * by key in the response and treats a miss — or a family with no variants — as
 * an ERROR rather than rendering whatever came back. A prescribe form that
 * silently offered another family's strengths would be a worse defect than the
 * one being fixed, and "the server would not do that" is not a control (R41).
 *
 * `reload` is exposed so the error state can offer a real retry: the clinician
 * is blocked until this read succeeds, so they must be able to try again without
 * losing the rest of the form.
 */
export function useFormulary(productKey: string | null | undefined): {
  formulary: FormularyState;
  reload: () => void;
} {
  const [formulary, setFormulary] = useState<FormularyState>({ state: 'idle' });
  const [attempt, setAttempt] = useState(0);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    const key = productKey?.trim();
    if (!key) {
      setFormulary({ state: 'idle' });
      return;
    }
    let cancelled = false;
    setFormulary({ state: 'loading' });
    getFormulary(key)
      .then((response) => {
        if (cancelled) return;
        const family = response.medications.find((m) => m.productKey === key);
        if (!family || family.variants.length === 0) {
          setFormulary({ state: 'error' });
          return;
        }
        setFormulary({ state: 'ready', family, source: response.source });
      })
      .catch(() => {
        if (!cancelled) setFormulary({ state: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [productKey, attempt]);

  return { formulary, reload };
}
